import { Color } from '@persona-id/design-tokens';
import { ReactNode, RefObject, useEffect, useMemo, VFC } from 'react';
import { useDrag, useDragLayer, XYCoord } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import { WorkflowStepCard } from 'dashboard/apps/InquiryTemplateEditor/WorkflowTree/WorkflowStepCard';
import { isWorkflowTreeUseDragLayer } from 'dashboard/dnd/types';
import { Z_INDICES } from 'dashboard/styles/variables';
import { AttachUndraggable } from 'lib/hooks/useDragScroll';
import { TokensToNode, WorkflowStepTreeNode } from 'lib/workflows';

export const KEY_DRAG_ITEM = 'draggableStep';
export type DraggedItem = { token: string };

const CustomDragLayer = styled('div')`
  position: fixed;
  pointer-events: none;
  z-index: 100;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
`;

const DraggedElement = styled.div<{
  currentPos: XYCoord;
  currentScale: number;
}>`
  position: absolute;
  display: ${({ currentPos }) => (currentPos ? 'block' : 'none')};
  ${({ currentPos, currentScale }) => css`
    top: ${currentPos?.y || 0}px;
    left: ${currentPos?.x || 0}px;
    transform: scale(${currentScale});
  `};
`;

const DraggingChildren = styled.span`
  text-align: right;
  position: absolute;
  margin-top: 48px;
  margin-left: 108px;
  z-index: ${Z_INDICES.tooltip};
  background-color: ${Color.CorePurple800};
  border-radius: ${BORDER_RADIUSES.pill};
  color: ${Color.PrimaryWhite};
  padding: 2px 8px;
`;

const countAllChildStep = (children: WorkflowStepTreeNode[]): number => {
  return children.reduce((sum, child) => {
    return sum + 1 + (child.children ? countAllChildStep(child.children) : 0);
  }, 0);
};

const SCROLL_AMOUNT = 10;

interface DragLayerProps {
  containerRef: RefObject<HTMLDivElement>;
  currentScale: number;
  tokensToNode: TokensToNode;
  onStartDragging: (item: string) => void;
  onEndDragging: () => void;
  panScreen: (translateX: number, translateY: number) => boolean;
}

export const DragLayer: VFC<DragLayerProps> = ({
  containerRef,
  currentScale,
  tokensToNode,
  onStartDragging,
  onEndDragging,
  panScreen,
}) => {
  const collectedProps = useDragLayer<GlobalUseDragLayer>((monitor) => ({
    currentPos: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
  }));

  const { currentPos, isDragging, item } = isWorkflowTreeUseDragLayer(collectedProps)
    ? collectedProps
    : { currentPos: null, isDragging: false, item: null };

  useEffect(() => {
    if (item) {
      onStartDragging(item.token);
    } else {
      onEndDragging();
    }
  }, [isDragging, onStartDragging, onEndDragging, collectedProps, item]);

  useEffect(() => {
    if (item && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();

      const interval = setInterval(() => {
        if (containerRect.x > currentPos.x) {
          panScreen(SCROLL_AMOUNT, 0);
        } else if (containerRect.width < currentPos.x) {
          panScreen(-SCROLL_AMOUNT, 0);
        }
        if (containerRect.y > currentPos.y) {
          panScreen(0, SCROLL_AMOUNT);
        } else if (containerRect.height < currentPos.y) {
          panScreen(0, -SCROLL_AMOUNT);
        }
      }, 50);

      return () => clearInterval(interval);
    }
  }, [containerRef, panScreen, collectedProps]);

  const draggedStep = item ? tokensToNode[item?.token] : null;

  const numberOfChildSteps = useMemo(() => {
    return draggedStep && draggedStep.children ? countAllChildStep(draggedStep.children) : 0;
  }, [draggedStep]);

  return currentPos && item && draggedStep ? (
    <CustomDragLayer>
      <DraggedElement currentPos={currentPos} currentScale={currentScale}>
        {draggedStep.children && (
          <DraggingChildren>
            {numberOfChildSteps === 1
              ? 'with a nested step'
              : `with ${numberOfChildSteps} nested steps`}
          </DraggingChildren>
        )}
        <WorkflowStepCard
          active={false}
          focused={false}
          hasError={false}
          name={draggedStep.step.name}
          token={item.token}
          type={draggedStep.step.type}
        />
      </DraggedElement>
    </CustomDragLayer>
  ) : null;
};

const WorkflowNodeDraggableContainer = styled('div')<{ isDragging: boolean }>`
  opacity: ${({ isDragging }) => (isDragging ? '0.25' : '1')};
  transition: opacity 0.5s;
`;

interface WithDraggableProps {
  token: string;
  attachUndraggable: AttachUndraggable;
  children: (ref: (e: HTMLDivElement) => void) => ReactNode;
}

export const WithDraggable = ({ token, children, attachUndraggable }: WithDraggableProps) => {
  const [{ isDragging }, drag, preview] = useDrag<DraggedItem, unknown, { isDragging: boolean }>(
    () => ({
      type: KEY_DRAG_ITEM,
      item: { token },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [token],
  );

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  // Forward the 'drag' ref to the actual compoennt to avoid showing unintentional margin around the component when dragging
  return (
    <WorkflowNodeDraggableContainer ref={attachUndraggable} isDragging={isDragging}>
      {children(drag)}
    </WorkflowNodeDraggableContainer>
  );
};
